/**
 * Alt yol (sub-path) farkindaligi — TUM ham konum islerinin tek gectigi yer.
 *
 * NEDEN AYRI BIR MODUL:
 * React Router `basename` verildiginde <Link> ve navigate() cagrilarini kendisi
 * duzeltir. AMA duzeltmedigi seyler var ve bunlar SESSIZCE bozulur:
 *
 *   window.location.assign('/giris')        -> /panel/ altinda 404
 *   window.location.pathname === '/giris'   -> hep false, yonlendirme kilitlenir
 *   fetch('/api/...') disindaki mutlak yollar
 *   <a href="/sozlesme">                    -> uygulamadan tamamen cikar
 *
 * Bunlarin hicbiri gelistirmede (base = '/') fark edilmez; yalniz uygulama bir
 * alt yoldan servis edildiginde ortaya cikar. Bu yuzden ham `window.location`
 * ve mutlak yol yazimi appPath/appUrl/stripBase uzerinden yapilir.
 *
 * `import.meta.env.BASE_URL` bu dosyada okunabilir (eslint istisnasi); Vite onu
 * her zaman bas ve son egik cizgiyle uretir ('/', '/panel/').
 */

// BASE_URL'in tum uygulamada okundugu TEK yer. eslint.config.js bu dosyayi
// (ve src/env.ts'i) import.meta.env yasagindan muaf tutar.
const RAW_BASE: string = import.meta.env.BASE_URL || '/';

/**
 * React Router `basename` degeri. Kok icin BOS DIZI degil bos string ('')
 * degil — router '/' bekler; birlestirme yaparken sondaki egik cizgi olmamali.
 * '/'        -> ''
 * '/panel/'  -> '/panel'
 */
export const BASENAME: string = RAW_BASE === '/' ? '' : RAW_BASE.replace(/\/+$/, '');

/** React Router'a verilecek deger ('' router icin gecersiz, '/' olmali). */
export const ROUTER_BASENAME: string = BASENAME || '/';

const withLeadingSlash = (path: string): string => (path.startsWith('/') ? path : `/${path}`);

/**
 * Uygulama ici yolu, sunucu kokune gore MUTLAK yola cevirir.
 * appPath('/giris') -> '/giris' veya '/panel/giris'
 */
export const appPath = (path: string): string => `${BASENAME}${withLeadingSlash(path)}`;

/**
 * appPath'in tam URL hali — window.location.assign / href icin.
 * Tarayici disinda (test, SSR) koken bilinmedigi icin gore yol dondurur.
 */
export const appUrl = (path: string): string => {
  const relative = appPath(path);
  if (typeof window === 'undefined') return relative;
  return `${window.location.origin}${relative}`;
};

/**
 * Ham bir konum yolundan taban yolu SOYAR — karsilastirma yapmadan once.
 * stripBase('/panel/giris') -> '/giris'
 * stripBase('/giris')       -> '/giris'
 *
 * `window.location.pathname === '/giris'` yerine
 * `stripBase(window.location.pathname) === '/giris'` yazilir.
 */
export const stripBase = (pathname: string): string => {
  if (!BASENAME) return withLeadingSlash(pathname);
  if (pathname === BASENAME) return '/';
  if (pathname.startsWith(`${BASENAME}/`)) {
    return withLeadingSlash(pathname.slice(BASENAME.length));
  }
  return withLeadingSlash(pathname);
};

/** Tarayicida tam sayfa yenilemesiyle gezinme (oturum sonlandirma gibi). */
export const hardNavigate = (path: string): void => {
  if (typeof window === 'undefined') return;
  window.location.assign(appUrl(path));
};
